/**
 * <s-newsletter-popup> — opens its child dialog once after a delay per visitor.
 *
 * Attributes:
 *   delay       — seconds before the popup opens (default 30)
 *   storage-key — localStorage key remembering dismissal/subscription
 *
 * After a successful newsletter post (Shopify appends customer_posted=true), the
 * popup opens immediately on its success pane so the visitor sees the code.
 */
class SNewsletterPopup extends HTMLElement {
  #timer = null;
  #ac = null;

  connectedCallback() {
    const dialog = this.querySelector('dialog');
    if (!dialog) return;

    this.#ac = new AbortController();
    const key = this.getAttribute('storage-key') || 'newsletter-popup';
    const delay = Math.max(0, Number(this.getAttribute('delay') || 30)) * 1000;
    const params = new URLSearchParams(window.location.search);
    const hasError = dialog.querySelector('[aria-invalid="true"]');

    // Editor previews should never auto-open the popup over the canvas.
    if (window.Shopify && window.Shopify.designMode) return;

    if (params.get('customer_posted') === 'true') {
      this.#remember(key, 'subscribed');
      this.#showSuccess();
      this.#open(dialog);
      return;
    }

    if (hasError) {
      this.#open(dialog);
      return;
    }

    if (this.#recall(key)) return;

    dialog.addEventListener('close', () => {
      if (!this.#recall(key)) this.#remember(key, 'dismissed');
    }, { signal: this.#ac.signal });

    this.#timer = window.setTimeout(() => this.#open(dialog), delay);
  }

  disconnectedCallback() {
    window.clearTimeout(this.#timer);
    this.#ac?.abort();
  }

  #open(dialog) {
    if (dialog.open || document.querySelector('dialog:modal')) return;
    dialog.showModal();
  }

  #showSuccess() {
    this.querySelectorAll('[data-popup-pane="form"]').forEach((el) => { el.hidden = true; });
    this.querySelectorAll('[data-popup-pane="success"]').forEach((el) => { el.hidden = false; });
  }

  #remember(key, value) {
    try { window.localStorage.setItem(key, value); } catch (e) { /* storage unavailable */ }
  }

  #recall(key) {
    try { return window.localStorage.getItem(key); } catch (e) { return null; }
  }
}

if (!customElements.get('s-newsletter-popup')) {
  customElements.define('s-newsletter-popup', SNewsletterPopup);
}
